import { ArrowRight } from '@untitledui/icons';

import {
  LightfieldBrandMark,
  ProviderLogoTile,
} from '@/components/integrations/ProviderBrandMarks';
import { Input } from '@/lib/base/forms/input/input';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';

import { LIGHTFIELD_API_KEYS_URL } from './constants';

interface LightfieldConnectStepProps {
  apiKey: string;
  onApiKeyChange: (value: string) => void;
  errorMessage: string | null;
}

export function LightfieldConnectStep({
  apiKey,
  onApiKeyChange,
  errorMessage,
}: LightfieldConnectStepProps) {
  return (
    <div className='flex flex-col gap-4'>
      {/* Brand lockup: where the data is coming from → where it's going. */}
      <div className='flex items-center gap-3'>
        <ProviderLogoTile>
          <LightfieldBrandMark className='size-5 text-fg-primary' />
        </ProviderLogoTile>
        <ArrowRight aria-hidden={true} className='size-4 text-fg-quaternary' />
        <ProviderLogoTile>
          <AhoyLogoMinimal className='size-5' />
        </ProviderLogoTile>
      </div>

      <p className='text-sm text-secondary'>
        Paste your Lightfield API key to connect. You can create one from your
        Lightfield settings.
      </p>

      <a
        href={LIGHTFIELD_API_KEYS_URL}
        target='_blank'
        rel='noopener noreferrer'
        aria-label='Get your Lightfield API key (opens in new tab)'
        className='text-utility-brand-600 hover:underline font-medium'
      >
        Get your Lightfield API key
      </a>

      <Input
        label='Lightfield API key'
        type='password'
        value={apiKey}
        onChange={onApiKeyChange}
        isInvalid={!!errorMessage}
        hint={errorMessage ?? undefined}
      />

      {/* Factual, verifiable claim — the key is stored AEAD-encrypted per
          dispatched job / import (apps/secrets) and deleted by post_save
          signals when each reaches a terminal state
          (lightfield_import/apps/signals.py). Don't strengthen this copy
          beyond what the backend actually does. */}
      <p className='text-xs text-tertiary'>
        Your key is only used to run this import. It's stored encrypted while
        the import runs, then deleted automatically.
      </p>
    </div>
  );
}
